export const analysisEs = {
  title: 'Perfiles de análisis y nutrición',
  subtitle:
    'Valores de laboratorio y calculados versionados, con trazabilidad, prioridad controlada y aprobación.',
  help: { open: 'Abrir ayuda sobre análisis, cálculos y aprobaciones' },
  pagination: 'Página {{page}} de {{pages}} · {{total}} versiones',
  actions: {
    parameters: 'Parámetros de análisis',
    newParameter: 'Nuevo parámetro',
    newLaboratory: 'Registrar perfil de laboratorio',
    calculate: 'Calcular receta',
    addValue: 'Añadir valor',
    submit: 'Enviar a revisión',
    review: 'Revisión técnica',
    approve: 'Aprobar',
    copy: 'Nueva versión',
    archive: 'Archivar',
    history: 'Historial de versiones',
  },
  filters: {
    searchProfiles: 'Buscar número, artículo o receta',
    searchParameters: 'Buscar clave o nombre',
    domain: 'Dominio, p. ej. NUTRITION',
    allStatuses: 'Todos los estados',
    allOrigins: 'Todos los orígenes',
  },
  statuses: {
    DRAFT: 'Borrador',
    IN_REVIEW: 'En revisión',
    APPROVED: 'Aprobado',
    SUPERSEDED: 'Sustituido',
    ARCHIVED: 'Archivado',
  },
  origins: {
    ANALYZED: 'Analizado',
    CALCULATED: 'Calculado',
    MIXED: 'Valor de laboratorio prioritario',
  },
  targetTypes: { PRODUCT: 'Artículo', RECIPE: 'Receta' },
  issueSeverity: { ERROR: 'Error', WARNING: 'Advertencia' },
  aggregationModes: {
    MASS_BALANCE: 'Balance de masas / cantidad aditiva',
    WEIGHTED_AVERAGE: 'Media ponderada',
    NOT_CALCULABLE: 'No calculable automáticamente',
  },
  roundingModes: { HALF_UP: 'Redondeo comercial', DOWN: 'Truncar', UP: 'Alejar de cero' },
  columns: {
    number: 'Número',
    target: 'Artículo / receta',
    domain: 'Dominio',
    origin: 'Origen',
    reference: 'Cantidad de referencia',
    version: 'Versión',
    status: 'Estado',
    issues: 'Errores / advertencias',
    key: 'Clave',
    name: 'Nombre',
    parameter: 'Parámetro',
    effective: 'Valor aprobado',
    calculated: 'Calculado',
    analyzed: 'Analizado',
    range: 'Mín / objetivo / máx / tolerancia',
    difference: 'Diferencia',
    hash: 'Hash de instantánea',
    created: 'Creado',
    validity: 'Validez',
  },
  fields: {
    key: 'Clave estable',
    domain: 'Dominio configurable',
    defaultUnit: 'Unidad del valor',
    reference: 'Cantidad y unidad de referencia',
    referenceQuantity: 'Cantidad de referencia',
    referenceUnit: 'Unidad de referencia',
    aggregation: 'Método de cálculo',
    retention: 'Factor de retención',
    rounding: 'Decimales / redondeo',
    metadata: 'Metadatos configurables (JSON)',
    changeReason: 'Motivo del cambio',
    validFrom: 'Válido desde',
    validTo: 'Válido hasta',
    name: 'Nombre',
    shortName: 'Nombre corto',
    description: 'Descripción',
    reason: 'Motivo / nota de revisión',
    number: 'Número de perfil',
    numberFirst: 'Número de perfil (solo primera versión)',
    targetType: 'Tipo de destino',
    target: 'Destino',
    source: 'Fuente / laboratorio / informe',
    method: 'Método de medición',
    sampledAt: 'Fecha de muestreo / medición',
    recipe: 'Versión de receta aprobada',
    laboratoryOverride: 'Versión de laboratorio aprobada con prioridad controlada',
    overrideReason: 'Motivo de prioridad del laboratorio',
    parameter: 'Parámetro de análisis',
    value: 'Valor',
    unitCode: 'Unidad',
    minValue: 'Mínimo',
    maxValue: 'Máximo',
    targetValue: 'Objetivo',
    toleranceValue: 'Tolerancia',
  },
  parameters: {
    title: 'Parámetros de análisis configurables',
    subtitle:
      'Parámetros neutrales por sector; los valores nutricionales se definen solo mediante dominios configurables.',
    createTitle: 'Crear parámetro de análisis',
    editTitle: 'Editar parámetro de análisis v{{version}}',
    editorHelp:
      'Todos los idiomas son obligatorios antes de la revisión. Unidades, retención y redondeo controlan cálculos reproducibles.',
    historyTitle: 'Historial del parámetro de análisis',
  },
  calculate: {
    title: 'Calcular valores de análisis desde una receta',
    description:
      'Resuelve subrecetas y considera cantidades, pérdidas, rendimiento, absorción, retención y reglas de redondeo.',
    noOverride: 'Usar solo valores calculados',
    reviewWarning:
      'El resultado es un borrador que requiere revisión. Las fuentes ausentes o caducadas nunca se sustituyen silenciosamente.',
  },
  laboratory: {
    title: 'Registrar perfil analizado',
    description:
      'Los valores manuales requieren una fuente trazable. Después, los informes se cargan solo en el expediente DMS protegido.',
    values: 'Valores de medición y análisis',
    valueHelp:
      'Mínimo, máximo, objetivo y tolerancia son opcionales; valor y unidad son obligatorios.',
    dmsHelp:
      'Tras crear el perfil, cargue informes, selecciónelos en el borrador y capture sus hashes.',
    editTitle: 'Editar borrador analizado',
    editDescription:
      'Los valores y la selección DMS usan bloqueo optimista. El contenido queda inmutable al enviarlo.',
  },
  review: {
    title: 'Se requiere revisión técnica',
    description:
      'Los valores calculados o de laboratorio prioritarios no son una aprobación técnica o legal automática. Deben revisarse fuentes, advertencias y desviaciones.',
  },
  sections: {
    values: 'Valores aprobados, calculados y de laboratorio',
    valuesHelp:
      'El valor aprobado es el efectivo de la instantánea. En MIXED se conserva el cálculo para compararlo.',
  },
  summary: {
    reference: 'Referencia',
    values: 'Parámetros',
    errors: 'Errores',
    warnings: 'Advertencias',
    validity: 'Validez',
  },
  tabs: {
    values: 'Valores',
    sources: 'Trazabilidad',
    validation: 'Validación',
    comparison: 'Comparación',
    documents: 'Informes',
    history: 'Versiones',
    snapshot: 'Instantánea',
  },
  sources: {
    manual: 'Valor introducido manualmente o analizado en laboratorio',
    noCalculated:
      'No existen contribuciones de material calculadas automáticamente para este valor.',
  },
  comparison: {
    title: 'Comparar versiones y valores de laboratorio/calculados',
    description:
      'Los valores se normalizan a unidades compatibles y se muestran diferencias decimales exactas.',
  },
  documents: {
    title: 'Informes de laboratorio y ensayo protegidos',
    snapshotSelection: 'Capturar documentos para este borrador',
    snapshotHelp:
      'Solo los archivos seleccionados y sus hashes SHA-256 forman parte de esta versión.',
  },
  history: {
    title: 'Historial de versiones inmutable',
    description:
      'Las versiones aprobadas siguen siendo reproducibles pese a cambios posteriores en las fuentes.',
  },
  snapshot: { sources: 'Instantánea de fuentes', calculation: 'Instantánea del cálculo' },
  states: {
    loading: 'Cargando valores de análisis…',
    noProfiles: 'Ninguna versión de perfil coincide con los filtros.',
    noParameters: 'Ninguna versión de parámetro coincide con los filtros.',
    noIssues: 'No hay errores ni advertencias de validación.',
    notFound: 'No se encontró la versión del perfil de análisis.',
  },
  messages: {
    saved: 'Borrador de análisis guardado.',
    created: 'Perfil de laboratorio creado.',
    calculated: 'Borrador de cálculo reproducible creado.',
    workflow: 'Flujo de análisis actualizado.',
  },
  workflow: {
    REVIEW: {
      title: 'Documentar revisión técnica',
      description: 'La persona revisora debe ser independiente del autor y del remitente.',
    },
    APPROVE: {
      title: 'Aprobar perfil de análisis',
      description:
        'La aprobación congela valores, fuentes, reglas, hashes documentales y advertencias.',
    },
    COPY: {
      title: 'Copiar versión analizada',
      description: 'Crea un nuevo borrador editable con trazabilidad completa.',
    },
    ARCHIVE: {
      title: 'Archivar versión de análisis',
      description: 'La versión auditable permanece legible y deja de estar activa.',
    },
  },
} as const;
